
import Vue from 'vue';
import getItemFromObject from 'lodash/get';
import { DataWithHref } from '@root/modules/headlines/types/headlines';
import { HeadlineLabel } from '@headlines/types/headline';
import { HeadlineLabels, HeadlineTitle, HeadlineImage, HeadlineLead, HeadlineIcons } from '@headlines/components/fragments';
import categoryUrl from '@root/modules/category/utils/buildCategoryUrl';
import { politicalAdCategories } from '@headlines/config/icons.config';

interface Data {
  leadLimit: number;
}

interface Props {
  headline: DataWithHref;
  pictureSrcset: Record<string, unknown>;
  template: string;
}

interface Methods {
  labels: (headline: DataWithHref) => (HeadlineLabel | undefined)[];
  ptContent: (headline: DataWithHref) => boolean;
  politicalAd: (headline: DataWithHref) => boolean;
  supportedContent: (headline: DataWithHref) => boolean;
}

export default Vue.extend<Data, Methods, unknown, Props>({
  components: {
    HeadlineLabels,
    HeadlineImage,
    HeadlineTitle,
    HeadlineLead,
    HeadlineIcons,
  },
  props: {
    headline: {
      type: Object,
      required: true,
    },
    pictureSrcset: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    template: {
      type: String,
      required: false,
      default: () => 'horizontal',
    },
  },
  data() {
    return {
      leadLimit: 140,
    };
  },
  methods: {
    labels(headline) {
      const labels = [];

      const { domain } = this.$channelConfig('settings');
      const sameChannel = domain === headline.primaryCategory.channel.url;
      const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;

      if (
        !sameChannel &&
        headline.primaryCategory?.channel.url !== 'www.delfi.lt' &&
        headline.primaryCategory?.channel.url !== 'www.delfi.lt/naujienos' &&
        headline.primaryCategory?.channel.url !== 'www.delfi.lt/projektai' &&
        headline.primaryCategory?.channel.url !== 'www.delfi.lt/uzsakomasis-turinys' &&
        headline.primaryCategory?.channel.url !== 'www.delfi.lt/multimedija'
      ) {
        labels.push({
          title: getItemFromObject(headline, 'primaryCategory.channel.name'),
          href: `/${headline.primaryCategory.channel.path || ''}`,
        });
      } else {
        labels.push({
          title: getItemFromObject(headline, 'primaryCategory.name'),
          href: categoryUrl({ category: headline.primaryCategory, domain, fakeDomainEnabled }),
        });
      }

      return labels;
    },
    ptContent(headline: DataWithHref) {
      return !!(
        (headline.settings?.find((setting) => setting.key === 'icon.reklama')?.value ?? false) ||
        (headline.settings?.find((setting) => setting.key === 'icon.sponsored_content')?.value ?? false) ||
        !!headline.settings?.find((setting) => setting.key === 'frontPageSponsoredContent' && setting.value === true) ||
        headline.primaryCategory.channel.url === 'www.delfi.lt/uzsakomasis-turinys'
      );
    },
    politicalAd(headline: DataWithHref) {
      return (
        politicalAdCategories.includes(headline.primaryCategory?.id) ||
        !!headline.tags?.items?.find((tag) => tag.id === 71865184) ||
        !!headline.settings?.find((setting) => setting.key === 'frontPagePoliticalAd' && setting.value === true)
      );
    },
    supportedContent(headline: DataWithHref) {
      return !!headline.settings?.find((setting) => setting.key === 'frontPageSupportedContent' && setting.value === true);
    },
  },
});
